import { Wifi, FileText, Download, Banknote, DollarSign, HandCoins, ArrowDownRight, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import FAQSection from "@/components/FAQSection";
import whyPerchGreenBg from "@/assets/why-perch-green-background.png";
import whyPerchImg1 from "@/assets/why-perch-img1.png";
import whyPerchImg2 from "@/assets/why-perch-img2.png";
import whyPerchImg3 from "@/assets/why-perch-img3.png";
import whyPerchImg4 from "@/assets/why-perch-img4.jpg";
import whyPerchImg5 from "@/assets/why-perch-img5.png";
import whyPerchImg6 from "@/assets/why-perch-img6.png";
import whyPerchImg7 from "@/assets/why-perch-img7.png";
import whyPerchImg8 from "@/assets/why-perch-img8.png";


const featureCards = [
  {
    icon: Wifi,
    title: "Fast 5G Speeds",
    description: "Same towers, real pricing. Reliable nationwide coverage.",
    highlight: false,
  },
  {
    icon: FileText,
    title: "Month-to-Month",
    description: "Cancel anytime. Your plan can change when your life does.",
    highlight: false,
  },
  {
    icon: Download,
    title: "Affordable Plans",
    description: "Get the lowest prices in wireless with plans starting at just $7.99/month. Quality service at unbeatable prices.",
    highlight: false,
  },
  {
    icon: Banknote,
    title: "Build Credit History",
    description: "Every on-time payment builds your credit score. No credit check required to get started.",
    highlight: true,
  },
];

const WhyPerchMobile = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121212' }}>
      <Seo
        title="Why Perch — Build credit while you stay connected"
        description="Perch Mobile reports your on-time phone payments to credit bureaus. Affordable 5G plans on AT&T's network that help you build credit history."
        path="/why-perch-mobile"
      />
      <Navigation />
      <main className="pt-20">
        {/* Hero Section - fit viewport so button is visible without scroll */}
        <section
          className="relative flex items-center overflow-hidden py-8 lg:py-12"
          style={{
            backgroundColor: '#131313',
            minHeight: 'calc(100vh - 5rem)',
            maxHeight: 'calc(100vh - 5rem)',
          }}
        >
          {/* Green blur ellipse - Figma: 310×310 at 1202,576; blur 215px */}
          <div
            className="absolute w-[310px] h-[310px] rounded-full pointer-events-none hidden lg:block"
            style={{
              right: 0,
              top: '576px',
              background: 'rgba(176, 241, 141, 0.5)',
              filter: 'blur(215px)',
            }}
          />

          <div className="w-full max-w-[1512px] mx-auto px-4 md:px-8 lg:px-[76px] z-10">
            <div className="flex items-center justify-between gap-8">
              {/* Left: Copy - Figma positions ~126–127px, widths 587–791px */}
              <div className="max-w-[791px] w-full lg:w-auto">
                {/* Who are we? - Figma: Rectangle 27 #4833C8, 171×38, radius 10px; Inter 600 18px */}
                <span
                  className="inline-flex items-center justify-center w-fit h-[38px] px-5 rounded-[10px] text-white mb-6"
                  style={{
                    backgroundColor: '#4833C8',
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: 18,
                    letterSpacing: '-0.05em',
                  }}
                >
                  Who are we?
                </span>
                {/* Title - Figma: 55px, line-height 65px, Instrument Sans 600, -0.05em, capitalize */}
                <h1
                  className="text-white mb-6 text-left capitalize"
                  style={{
                    fontFamily: 'Instrument Sans',
                    fontWeight: 600,
                    fontSize: 55,
                    lineHeight: '65px',
                    letterSpacing: '-0.05em',
                  }}
                >
                  Mobile service, from a better{" "}
                  <span style={{ color: '#B0F18D' }}>vantage point.</span>
                </h1>
                {/* Body - Figma: 587px width, Inter 600 25px, line-height 39px */}
                <p
                  className="text-white mb-8 text-left capitalize max-w-[587px]"
                  style={{
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: 25,
                    lineHeight: '39px',
                    letterSpacing: '-0.05em',
                  }}
                >
                  Perch is built for self-investors. Your payments build credit. Your unused data stays yours. Same trusted network, designed to work for you, not against you.
                </p>
                {/* Button - scrolls to Our Mission section */}
                <a
                  href="#our-mission"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('our-mission')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="inline-block"
                >
                  <Button
                    className="w-[184px] h-[59px] rounded-[11px] font-semibold"
                    style={{
                      backgroundColor: '#B0F18D',
                      color: '#000000',
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: 20,
                      lineHeight: '34px',
                      letterSpacing: '-0.05em',
                    }}
                  >
                    Our Mission
                  </Button>
                </a>
              </div>

              {/* Right: 2x2 grid - top 2 complete, bottom 2 half (cropped) */}
              <div className="hidden lg:block flex-shrink-0 relative">
                <div
                  className="relative grid grid-cols-[289px_300px] gap-x-[28px] gap-y-[33px]"
                  style={{ width: 617 }}
                >
                  {/* Top-left - full */}
                  <div className="rounded-[40px] overflow-hidden w-[289px] h-[450px]">
                    <img
                      src={whyPerchImg6}
                      alt="Person on phone, active lifestyle"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Top-right - full */}
                  <div className="rounded-[40px] overflow-hidden w-[300px] h-[450px] mt-[86px]">
                    <img
                      src={whyPerchImg2}
                      alt="Person relaxed, satisfied"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Bottom-left - half (upper half only) */}
                  <div className="rounded-[40px] overflow-hidden w-[289px] h-[225px]">
                    <img
                      src={whyPerchImg4}
                      alt="Group of people together"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  {/* Bottom-right - half (upper half only) */}
                  <div className="rounded-[40px] overflow-hidden w-[300px] h-[225px]">
                    <img
                      src={whyPerchImg3}
                      alt="Person celebrating with phone"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Mission Section - Figma: #27243A, centered, gradient text */}
        <section id="our-mission" className="relative py-20 md:py-24 scroll-mt-24 overflow-hidden" style={{ backgroundColor: '#27243A' }}>
          <div className="absolute w-[474px] h-[474px] rounded-full pointer-events-none opacity-80" style={{ left: -95, top: 660, background: 'rgba(237, 255, 145, 0.1)', filter: 'blur(215px)' }} />
          <div className="absolute w-[310px] h-[310px] rounded-full pointer-events-none" style={{ left: -29, top: 104, background: 'rgba(176, 241, 141, 0.5)', filter: 'blur(215px)' }} />
          <div className="w-full max-w-[1512px] mx-auto px-4 md:px-8 lg:px-[76px] relative z-10">
            <div className="flex flex-col items-center text-center">
              <span className="inline-flex items-center justify-center w-fit h-[38px] px-5 rounded-[10px] text-white mb-6" style={{ backgroundColor: '#4833C8', fontFamily: 'Inter', fontWeight: 600, fontSize: 18, letterSpacing: '-0.05em' }}>
                Our Mission
              </span>
              <h2 className="text-white mb-6 capitalize max-w-[679px]" style={{ fontFamily: 'Instrument Sans', fontWeight: 600, fontSize: 'clamp(32px, 4vw, 55px)', lineHeight: '65px', letterSpacing: '-0.05em' }}>
                Your phone bill can be more than just another <span style={{ color: '#B0F18D' }}>Expense</span>
              </h2>
              <p className="text-white/70 mb-8 capitalize max-w-[826px] text-center" style={{ fontFamily: 'Inter', fontWeight: 600, fontSize: 'clamp(18px, 2vw, 25px)', lineHeight: '44px', letterSpacing: '-0.05em' }}>
                Perch is helping you reach what matters by building your credit history with every phone payment. Imagine that apartment you want, the car you need, the future you're building, all within reach. This is mobile service for self-investors.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mb-8">
                {/* Overlapping circles: 3 avatars + 1 purple icon circle */}
                <div className="flex items-center">
                  {[
                    whyPerchImg5,
                    whyPerchImg7,
                    whyPerchImg8,
                  ].map((src, i) => (
                    <div
                      key={i}
                      className="w-[54px] h-[54px] rounded-full border-2 border-white/70 overflow-hidden flex-shrink-0 shadow-[0px_4px_84px_rgba(0,0,0,0.25)]"
                      style={{ marginLeft: i === 0 ? 0 : -16 }}
                    >
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                  <div
                    className="w-[54px] h-[54px] rounded-full border-2 border-white/70 flex items-center justify-center flex-shrink-0 shadow-[0px_4px_84px_rgba(0,0,0,0.25)]"
                    style={{ marginLeft: -16, backgroundColor: '#4833C8' }}
                  >
                    <HandCoins className="w-6 h-6 text-white" strokeWidth={2} />
                  </div>
                </div>
                <p className="capitalize" style={{ fontFamily: 'Instrument Sans', fontWeight: 600, fontSize: 'clamp(24px, 3vw, 40px)', lineHeight: '57px', letterSpacing: '-0.05em', background: 'linear-gradient(90.11deg, #FFFFFF 12.78%, #999999 95.05%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Mobile service for self-investors.
                </p>
              </div>
              {/* <Link to="/plans">
                <Button className="w-[184px] h-[59px] rounded-[11px] font-semibold capitalize" style={{ backgroundColor: '#533DDA', color: '#FFFFFF', fontFamily: 'Inter', fontWeight: 600, fontSize: 20, lineHeight: '34px', letterSpacing: '-0.05em' }}>
                  Our Mission
                </Button>
              </Link> */}
            </div>
          </div>
        </section>

        {/* Features - Here's What You Actually Get - Figma: 5 cards, 272px, gap 22px */}
        <section className="relative py-16 md:py-20" style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
          <div className="w-full max-w-[1512px] mx-auto px-4 md:px-8 lg:px-[76px]">
            <h2 className="text-center text-white mb-16 capitalize" style={{ fontFamily: 'Instrument Sans', fontWeight: 600, fontSize: 'clamp(28px, 4vw, 55px)', lineHeight: '65px', letterSpacing: '-0.05em' }}>
              Here's What You Actually Get.
            </h2>
            <div className="flex flex-nowrap justify-center gap-[22px]">
              {featureCards.map((card, index) => {
                const Icon = card.icon;
                return (
                  <div
                    key={index}
                    className="flex-1 min-w-0 max-w-[272px] rounded-[40px] p-4 md:p-6 flex flex-col min-h-[280px] md:min-h-[320px] backdrop-blur-[10px] shadow-[0px_14px_84px_30px_rgba(0,0,0,0.15)]"
                    style={{
                      background: card.highlight
                        ? 'linear-gradient(0deg, #B0F18D, #B0F18D), linear-gradient(133.48deg, rgba(82, 82, 82, 0.7) 50.8%, rgba(205, 205, 205, 0.224) 109.02%)'
                        : 'linear-gradient(0deg, rgba(0, 0, 0, 0.13), rgba(0, 0, 0, 0.13)), linear-gradient(133.48deg, rgba(82, 82, 82, 0.7) 50.8%, rgba(205, 205, 205, 0.224) 109.02%)',
                    }}
                  >
                    <div className="w-9 h-9 rounded-full flex items-center justify-center mb-4 flex-shrink-0" style={{ backgroundColor: '#533DDA' }}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3
                      className="mb-3 capitalize"
                      style={{
                        fontFamily: 'Instrument Sans',
                        fontWeight: 600,
                        fontSize: 'clamp(16px, 2.2vw, 40px)',
                        lineHeight: '1.1',
                        letterSpacing: '-0.05em',
                        color: card.highlight ? '#4833C8' : '#FFFFFF',
                      }}
                    >
                      {card.title}
                    </h3>
                    <p
                      className="flex-1 text-sm md:text-base font-semibold capitalize"
                      style={{
                        fontFamily: 'Instrument Sans',
                        letterSpacing: '-0.05em',
                        color: card.highlight ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
                        lineHeight: '1.25',
                      }}
                    >
                      {card.description}
                    </p>
                    <div className="mt-4 flex justify-end">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${card.highlight ? 'bg-black' : 'border border-white/20'}`}
                      >
                        <ArrowDownRight className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA - Ready to Move Forward - green background image (dulled for text contrast) */}
        <section
          className="py-16 md:py-20 relative overflow-hidden bg-cover bg-center"
          style={{
            backgroundImage: `url(${whyPerchGreenBg})`,
            backgroundColor: '#B0F18D',
          }}
        >
          {/* Dull overlay so text is clearly visible */}
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundColor: 'rgba(176, 241, 141, 0.55)',
            }}
          />
          <div className="w-full max-w-[1512px] mx-auto px-4 md:px-8 lg:px-[76px] relative z-10">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
              <div className="max-w-[719px]">
                <h2 className="text-black mb-4 capitalize" style={{ fontFamily: 'Instrument Sans', fontWeight: 600, fontSize: 'clamp(32px, 4vw, 55px)', lineHeight: '65px', letterSpacing: '-0.05em' }}>
                  Ready to Move Forward?
                </h2>
                <p className="text-black mb-8 capitalize" style={{ fontFamily: 'Instrument Sans', fontWeight: 600, fontSize: 35, lineHeight: '65px', letterSpacing: '-0.05em' }}>
                  Your money. Your data. Your future.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/plans">
                    <Button className="w-[184px] h-[59px] rounded-[11px] font-semibold capitalize" style={{ backgroundColor: '#533DDA', color: '#FFFFFF', fontFamily: 'Inter', fontWeight: 600, fontSize: 20, lineHeight: '34px', letterSpacing: '-0.05em' }}>
                      Switch to Perch
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="w-full max-w-[486px] flex-shrink-0 flex justify-center lg:justify-end overflow-hidden rounded-[46px]">
                <img
                  src={whyPerchImg1}
                  alt="Data plans: 1 GB, 5 GB, 12 GB, unlimited"
                  className="w-full max-w-[400px] h-auto object-contain rounded-[46px]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section - same as Index page */}
        <div id="faq" className="scroll-mt-24">
          <FAQSection />
        </div>

        {/* Contact strip - Figma: #533DDA bar with email, phone, address */}
        {/* <section className="w-full py-6 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 flex-wrap" style={{ backgroundColor: '#533DDA' }}>
          <a href="mailto:support@perchmobile.com" className="flex items-center gap-2 text-white font-semibold hover:opacity-90" style={{ fontFamily: 'Inter', fontSize: 20, letterSpacing: '-0.05em' }}>
            <Mail className="w-6 h-6 border border-white/50 rounded-full p-1" />
            support@perchmobile.com
          </a>
          <a href="tel:8337365690" className="flex items-center gap-2 text-white font-semibold hover:opacity-90" style={{ fontFamily: 'Inter', fontSize: 20, letterSpacing: '-0.05em' }}>
            <Phone className="w-6 h-6 border border-white/50 rounded-full p-1" />
            Text 833-736-5690
          </a>
          <span className="flex items-center gap-2 text-white font-semibold" style={{ fontFamily: 'Inter', fontSize: 20, letterSpacing: '-0.05em' }}>
            <MapPin className="w-6 h-6 border border-white/50 rounded-full p-1" />
            195 Montague St Brooklyn NY 11201
          </span>
        </section> */}
      </main>
      <Footer />
    </div>
  );
};

export default WhyPerchMobile;
